import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { getPaginasDeReferencia } from '../../../../api/reportApi';
import TableContainer from '../../../../components/TableContainerReactTable';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';
import AppMetaBadge from './AppMetaBadge';

const SOCIAL_DOMAINS = ['facebook.com', 'instagram.com', 'twitter.com', 'linkedin.com', 'tiktok.com'];
const SEARCH_DOMAINS = ['google.com', 'bing.com', 'yahoo.com', 'duckduckgo.com'];

const normalizeReferrerHostname = (rawHostname: unknown): string => {
  if (rawHostname === null || rawHostname === undefined || rawHostname === 'null') {
    return '(Direto)';
  }

  return String(rawHostname);
};

const isDirectReferrer = (rawHostname: unknown): boolean => normalizeReferrerHostname(rawHostname) === '(Direto)';

interface ReferrerCardProps {
  campaignId: string;
}

export interface ReferrerCardRef {
  refresh: () => void;
}

const ReferrerCard = forwardRef<ReferrerCardRef, ReferrerCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const referrerData = await getPaginasDeReferencia(parseInt(campaignId));
        setData(referrerData);
      } catch (err) {
        console.error('Error fetching referrer data:', err);
        setError('Erro ao carregar dados de páginas de referência');
      } finally {
        setLoading(false);
      }
    }, [campaignId]);

    useEffect(() => {
      if (campaignId) {
        fetchData();
      }
    }, [campaignId, fetchData]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={`${styles.reportCard} h-full w-full`}>
          <CardHeader className={styles.cardHeader}>
            <div className="flex items-center">
              <div className="bg-info bg-opacity-10 rounded-full p-2 mr-3">
                <i className="ri-external-link-line text-info text-2xl"></i>
              </div>
              <div>
                <h4 className={`${styles.cardTitle} mb-0`}>Origem do Tráfego</h4>
                <small className="text-muted">De onde vêm os usuários</small>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center text-destructive">
              <i className="ri-error-warning-line mr-2"></i>
              {error}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={`${styles.reportCard} h-full w-full`}>
          <CardHeader className={styles.cardHeader}>
            <div className="flex items-center">
              <div className="bg-info bg-opacity-10 rounded-full p-2 mr-3">
                <i className="ri-external-link-line text-info text-2xl"></i>
              </div>
              <div>
                <h4 className={`${styles.cardTitle} mb-0`}>Origem do Tráfego</h4>
                <small className="text-muted">De onde vêm os usuários</small>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted">
              <i className="ri-information-line mr-2"></i>
              Nenhuma origem de tráfego identificada
            </div>
          </CardContent>
        </Card>
      );
    }

    // Column definitions for the table
    const columns = [
      {
        header: 'Origem do Tráfego',
        accessorKey: 'referrerHostname',
        cell: (row: any) => {
          const original = row.row.original;
          const hostname = normalizeReferrerHostname(row.getValue());

          if (original.isApp) {
            return <AppMetaBadge label={original.appName || hostname} iconUrl={original.iconUrl} />;
          }

          const isDirect = hostname === '(Direto)';
          const hostnameLower = hostname.toLowerCase();
          const isSocial = SOCIAL_DOMAINS.some(social =>
            hostnameLower.includes(social)
          );
          const isSearch = SEARCH_DOMAINS.some(search =>
            hostnameLower.includes(search)
          );

          let icon = 'ri-global-line';
          let color = 'text-muted';

          if (isDirect) {
            icon = 'ri-home-line';
            color = 'text-primary';
          } else if (isSocial) {
            icon = 'ri-share-line';
            color = 'text-info';
          } else if (isSearch) {
            icon = 'ri-search-line';
            color = 'text-success';
          }

          return (
            <div className="flex items-center">
              <i className={`${icon} ${color} mr-2`}></i>
              <span className="font-medium">{hostname}</span>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        id: 'impression',
        header: 'Impressões',
        accessorKey: '_value',
        cell: (row: any) => {
          const value = row.getValue() || 0;
          const total = data.reduce((sum, item) => sum + (item._value || 0), 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';

          return (
            <div className="flex items-center justify-between">
              <span className="font-semibold">{numeral(value).format(numeralInteger)}</span>
              <Badge variant="secondary" className="bg-primary bg-opacity-10 text-primary px-2 py-1">
                {percentage}%
              </Badge>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    return (
      <Card className={`${styles.reportCard} h-full w-full`}>
        <CardHeader className={styles.cardHeader}>
          <div className="flex items-center">
            <div className="bg-info bg-opacity-10 rounded-full p-2 mr-3">
              <i className="ri-external-link-line text-info text-2xl"></i>
            </div>
            <div>
              <h4 className={`${styles.cardTitle} mb-0`}>Origem do Tráfego</h4>
              <small className="text-muted">De onde vêm os usuários</small>
            </div>
            <div className="ml-auto">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <i
                      className="ri-question-line text-muted text-lg cursor-help"
                    ></i>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Mostra de onde os usuários vieram antes de acessar seu anúncio: redes sociais, outros sites, busca direta, etc.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards - Compact Layout */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="flex items-center p-2 bg-muted rounded-lg border">
              <div className="bg-primary bg-opacity-10 rounded-full p-1 mr-2">
                <i className="ri-global-line text-primary"></i>
              </div>
              <div className="flex-grow-1">
                <div className="font-bold text-primary text-lg">{data.length}</div>
                <small className="text-muted">Fontes</small>
              </div>
            </div>
            <div className="flex items-center p-2 bg-muted rounded-lg border">
              <div className="bg-success bg-opacity-10 rounded-full p-1 mr-2">
                <i className="ri-share-line text-success"></i>
              </div>
              <div className="flex-grow-1">
                <div className="font-bold text-success text-lg">
                  {data.filter(item => {
                    const hostname = item.referrerHostname?.toLowerCase() || '';
                    return SOCIAL_DOMAINS.some(social =>
                      hostname.includes(social)
                    );
                  }).length}
                </div>
                <small className="text-muted">Sociais</small>
              </div>
            </div>
            <div className="flex items-center p-2 bg-muted rounded-lg border">
              <div className="bg-info bg-opacity-10 rounded-full p-1 mr-2">
                <i className="ri-search-line text-info"></i>
              </div>
              <div className="flex-grow-1">
                <div className="font-bold text-info text-lg">
                  {data.filter(item => {
                    const hostname = item.referrerHostname?.toLowerCase() || '';
                    return SEARCH_DOMAINS.some(search =>
                      hostname.includes(search)
                    );
                  }).length}
                </div>
                <small className="text-muted">Busca</small>
              </div>
            </div>
            <div className="flex items-center p-2 bg-muted rounded-lg border">
              <div className="bg-warning bg-opacity-10 rounded-full p-1 mr-2">
                <i className="ri-home-line text-warning"></i>
              </div>
              <div className="flex-grow-1">
                <div className="font-bold text-warning text-lg">
                  {data.filter(item => {
                    return isDirectReferrer(item.referrerHostname);
                  }).length}
                </div>
                <small className="text-muted">Direto</small>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="mt-3">
            <TableContainer
              divClass="table-responsive"
              columns={columns}
              data={data}
              isGlobalFilter={false}
              customPageSize={5}
              initialSorting={[{ id: 'impression', desc: true }]}
            />
          </div>
        </CardContent>
      </Card>
    );
  }
);

ReferrerCard.displayName = 'ReferrerCard';

export default ReferrerCard;
